import { http } from '@/api/core/request';
import { ApiUrls } from '@/api/endpoints/apiUrls';
import {
  CreateDirectChatParams,
  UnackedMessagesParams,
  AblyTokenResult,
  GetMessagesParams,
  MessagesListResult,
  ConversationInfo,
  PinGroupMessageParams
} from '@/api/types';

/**
 * 聊天服务
 * 处理聊天相关的API请求
 */
export class ChatService {
  /**
   * 获取Ably令牌
   * @returns Ably令牌
   */
  static async getAblyToken(): Promise<AblyTokenResult> {
    return http.post<AblyTokenResult>(ApiUrls.CHAT.ABLY_TOKEN);
  }

  /**
   * 创建私聊
   * @param params 创建私聊参数
   * @returns 空响应
   */
  static async createDirectChat(params: CreateDirectChatParams): Promise<void> {
    return http.post<void>(ApiUrls.CHAT.DIRECT, params);
  }

  /**
   * 获取用户对话列表
   * @returns 对话列表
   */
  static async getConversations(): Promise<ConversationInfo[]> {
    return http.get<ConversationInfo[]>(ApiUrls.CHAT.CONVERSATIONS);
  }

  /**
  * 置顶群组消息
  * @param groupId 群组ID
  * @param params 消息ID参数
  * @returns 空响应
  */
  static async pinGroupMessage(groupId: string, params: PinGroupMessageParams): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.PIN_MESSAGE, { group_id: groupId });
    return http.post<void>(url, params);
  }

  /**
   * 取消置顶群组消息
   * @param groupId 群组ID
   * @returns 空响应
   */
  static async unpinGroupMessage(groupId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.UNPIN_MESSAGE, { group_id: groupId });
    return http.post<void>(url);
  }

  /**
   * 获取未确认消息
   * @param conversationId 对话ID
   * @param params 查询参数
   * @returns 未确认消息列表
   */
  static async getUnackedMessages(conversationId: string, params?: UnackedMessagesParams): Promise<MessagesListResult> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.UNACKED_MESSAGES, { conversation_id: conversationId });
    return http.get<MessagesListResult>(url, params);
  }

  /**
   * 获取消息
   * @param conversationId 对话ID
   * @param params 查询参数
   * @returns 消息列表
   */
  static async getMessages(conversationId: string, params?: GetMessagesParams): Promise<MessagesListResult> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.GET_MESSAGES, { conversation_id: conversationId });
    return http.get<MessagesListResult>(url, params);
  }

  /**
   * 删除消息
   * @param conversationId 对话ID
   * @param messageId 消息ID
   * @returns 空响应
   */
  static async deleteMessage(conversationId: string, messageId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.DELETE_MESSAGE, {
      conversation_id: conversationId,
      message_id: messageId
    });
    return http.delete<void>(url);
  }

  /**
   * 删除会话历史记录
   * @param conversationId 对话ID
   * @returns 空响应
   */
  static async deleteConversationHistory(conversationId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.DELETE_HISTORY, { conversation_id: conversationId });
    return http.post<void>(url);
  }

  /**
   * 置顶对话
   * @param conversationId 对话ID
   * @returns 空响应
   */
  static async pinConversation(conversationId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.PIN_CONVERSATION, { conversation_id: conversationId });
    return http.post<void>(url);
  }

  /**
   * 取消置顶对话
   * @param conversationId 对话ID
   * @returns 空响应
   */
  static async unpinConversation(conversationId: string): Promise<void> {
    const url = ApiUrls.getUrl(ApiUrls.CHAT.UNPIN_CONVERSATION, { conversation_id: conversationId });
    return http.post<void>(url);
  }
}
